import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiUser, FiMail, FiBriefcase, FiCalendar, FiClock, FiFileText, FiMessageSquare, FiAlertCircle, FiCheckCircle, FiTrash2, FiGift, FiInfo, FiSettings } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { getUserDetails, updateUserMembership, deleteUser, giftCreditsToUser, assignUserToMentor, getAllMentors } from '../../services/adminService';
import Tooltip from '../../components/Tooltip';

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [creditsToGift, setCreditsToGift] = useState(0);
  const [giftingCredits, setGiftingCredits] = useState(false);
  const [mentors, setMentors] = useState([]);
  const [selectedMentor, setSelectedMentor] = useState('');
  const [loadingMentors, setLoadingMentors] = useState(false);
  const [assigningMentor, setAssigningMentor] = useState(false);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        setLoading(true);
        const response = await getUserDetails(userId);
        setUser(response.data);
        setSelectedMembership(response.data.membership_type);
        
        // If the user has a mentor, set the selectedMentor state
        if (response.data.mentor_id) {
          setSelectedMentor(response.data.mentor_id);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching user details:', err);
        setError('Failed to load user details. Please try again later.');
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [userId]);
  
  // Fetch all mentors
  useEffect(() => {
    const fetchMentors = async () => {
      try {
        setLoadingMentors(true);
        // Fetch users with mentor role using the new API endpoint
        const response = await getAllMentors();
        setMentors(response.data.data || []);
        setLoadingMentors(false);
      } catch (err) {
        console.error('Error fetching mentors:', err);
        setLoadingMentors(false);
      }
    };
    
    fetchMentors();
  }, []);

  const handleMembershipChange = (e) => {
    setSelectedMembership(e.target.value);
    setShowConfirmation(true);
  };

  const handleUpdateMembership = async () => {
    try {
      setUpdating(true);
      const response = await updateUserMembership(userId, selectedMembership);
      setUser(response.data);
      setUpdating(false);
      setShowConfirmation(false);
      setUpdateSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setUpdateSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error updating user membership:', err);
      setError('Failed to update membership. Please try again.');
      setUpdating(false);
    }
  };
  
  const handleDeleteUser = async () => {
    try {
      setUpdating(true);
      await deleteUser(userId);
      setUpdating(false);
      toast.success('User deleted successfully');
      navigate('/admin/users');
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user. Please try again.');
      setUpdating(false);
      setShowDeleteConfirmation(false);
    }
  };
  
  const handleGiftCredits = async () => {
    if (creditsToGift <= 0) {
      toast.error('Please enter a valid number of credits');
      return;
    }
    
    try {
      setGiftingCredits(true);
      const response = await giftCreditsToUser(userId, creditsToGift);
      setUser(response.data);
      setGiftingCredits(false);
      setCreditsToGift(0);
      toast.success(`Successfully gifted ${creditsToGift} credits to ${user.full_name}`);
    } catch (err) {
      console.error('Error gifting credits:', err);
      toast.error('Failed to gift credits. Please try again.');
      setGiftingCredits(false);
    }
  };
  
  const handleAssignMentor = async () => {
    if (!selectedMentor) {
      toast.error('Please select a mentor');
      return;
    }
    
    try {
      setAssigningMentor(true);
      const response = await assignUserToMentor(userId, selectedMentor);
      setUser(response.data);
      setAssigningMentor(false);
      toast.success(`Successfully assigned ${user.full_name} to mentor`);
    } catch (err) {
      console.error('Error assigning mentor:', err);
      toast.error('Failed to assign mentor. Please try again.');
      setAssigningMentor(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <FiAlertCircle className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <FiAlertCircle className="h-5 w-5 text-yellow-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">User not found.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">User Details</h1>
        <div className="flex space-x-3">
          {user.membership_type === 'mentor' && (
            <button
              type="button"
              onClick={() => navigate(`/admin/mentors/${userId}/settings`)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <FiSettings className="mr-2" />
              Mentor Settings
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate('/admin/users')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Back to Users
          </button>
        </div>
      </div>

      {/* Success Message */}
      {updateSuccess && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <FiCheckCircle className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">Membership updated successfully!</p>
            </div>
          </div>
        </div>
      )}

      {/* User Profile */}
      <div className="card overflow-hidden">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">User Profile</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">Personal details and account information.</p>
          </div>
          <div className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
            ${user.membership_type === 'gold' ? 'bg-yellow-100 text-yellow-800' : 
              user.membership_type === 'platinum' ? 'bg-green-100 text-green-800' : 
              user.membership_type === 'admin' ? 'bg-primary-100 text-primary-800' : 
              'bg-gray-100 text-gray-800'}`}>
            {user.membership_type}
          </div>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <FiUser className="mr-2" /> Full Name
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{user.full_name}</dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <FiMail className="mr-2" /> Email address
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{user.email}</dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <FiCalendar className="mr-2" /> Account created
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {new Date(user.created_at).toLocaleString()}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <FiClock className="mr-2" /> Last login
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Polish Usage */}
      <div className="card overflow-hidden">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Polish Usage</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">Current usage and limits.</p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Used Count</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {user.polish_usage?.used_count || 0} / {user.polish_usage?.max_count || 0}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Available Credits</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {user.polish_usage?.credits || 0}
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Lifetime Credits</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {user.polish_usage?.lifetime_credits || 0}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Reset Date</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {user.polish_usage?.reset_date ? new Date(user.polish_usage.reset_date).toLocaleDateString() : 'N/A'}
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                Gift Credits
                <Tooltip text="Add credits to user's account" position="right">
                  <FiInfo className="ml-1 text-gray-400" />
                </Tooltip>
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    min="1"
                    value={creditsToGift}
                    onChange={(e) => setCreditsToGift(parseInt(e.target.value) || 0)}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-24 sm:text-sm border-gray-300 rounded-md"
                    placeholder="Credits"
                  />
                  <Tooltip text="Gift credits to this user" position="bottom">
                    <button
                      type="button"
                      onClick={handleGiftCredits}
                      disabled={giftingCredits || creditsToGift <= 0}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                    >
                      <FiGift className="mr-2" />
                      {giftingCredits ? 'Gifting...' : 'Gift Credits'}
                    </button>
                  </Tooltip>
                </div>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* User Content Stats */}
      <div className="card overflow-hidden">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Content Statistics</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">User's content and activity.</p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <FiFileText className="mr-2" /> Resumes
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{user.resumes_count || 0}</dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <FiFileText className="mr-2" /> Cover Letters
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{user.cover_letters_count || 0}</dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <FiBriefcase className="mr-2" /> Applications
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{user.applications_count || 0}</dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <FiMessageSquare className="mr-2" /> Support Tickets
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{user.tickets_count || 0}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card overflow-hidden">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Recent Activity</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">Latest actions and updates.</p>
        </div>
        <div className="border-t border-gray-200">
          {user.recent_activity && user.recent_activity.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {user.recent_activity.map((activity, index) => (
                <li key={index} className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-primary-600">{activity.type}</div>
                    <div className="text-sm text-gray-500">{new Date(activity.timestamp).toLocaleString()}</div>
                  </div>
                  <div className="mt-2 text-sm text-gray-500">{activity.description}</div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-5 sm:px-6 text-center text-gray-500">
              No recent activity found.
            </div>
          )}
        </div>
      </div>

      {/* Membership Management */}
      <div className="card overflow-hidden">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Membership Management</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">Update user's membership type.</p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <div className="sm:grid sm:grid-cols-3 sm:gap-4">
            <div className="text-sm font-medium text-gray-500">Current Membership</div>
            <div className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              <select
                id="membership-type"
                name="membership-type"
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                value={selectedMembership}
                onChange={handleMembershipChange}
              >
                <option value="community">Community</option>
                <option value="gold">Gold</option>
                <option value="platinum">Platinum</option>
                <option value="mentor">Mentor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          
          {/* Mentor Assignment - Only show for non-mentor users */}
          {selectedMembership !== 'mentor' && selectedMembership !== 'admin' && (
            <div className="sm:grid sm:grid-cols-3 sm:gap-4 mt-6">
              <div className="text-sm font-medium text-gray-500 flex items-center">
                Assign to Mentor
                <Tooltip text="Assign this user to a mentor" position="right">
                  <FiInfo className="ml-1 text-gray-400" />
                </Tooltip>
              </div>
              <div className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <div className="flex items-center space-x-3">
                  <select
                    id="mentor-select"
                    name="mentor-select"
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                    value={selectedMentor}
                    onChange={(e) => setSelectedMentor(e.target.value)}
                    disabled={loadingMentors}
                  >
                    <option value="">Select a mentor</option>
                    {mentors.map((mentor) => (
                      <option key={mentor.id} value={mentor.id}>
                        {mentor.full_name} ({mentor.email})
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAssignMentor}
                    disabled={assigningMentor || !selectedMentor}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                  >
                    {assigningMentor ? 'Assigning...' : 'Assign'}
                  </button>
                </div>
                {user.mentor && (
                  <div className="mt-2 text-sm text-gray-500">
                    Currently assigned to: <span className="font-medium">{user.mentor.full_name}</span>
                  </div>
                )}
                {loadingMentors && (
                  <div className="mt-2 text-sm text-gray-500">
                    Loading mentors...
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Confirmation Dialog */}
          {showConfirmation && (
            <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <FiAlertCircle className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    Are you sure you want to change this user's membership to <strong>{selectedMembership}</strong>?
                    This will reset their polish usage counts.
                  </p>
                  <div className="mt-4">
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        onClick={handleUpdateMembership}
                        disabled={updating}
                      >
                        {updating ? 'Updating...' : 'Confirm'}
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        onClick={() => {
                          setSelectedMembership(user.membership_type);
                          setShowConfirmation(false);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Account Management */}
      <div className="card overflow-hidden">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Account Management</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">Danger zone - delete user account.</p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <div className="sm:flex sm:items-center sm:justify-between">
            <div>
              <h3 className="text-sm font-medium text-red-800">Delete User Account</h3>
              <p className="text-sm text-gray-500">
                This action cannot be undone. All user data will be permanently deleted.
              </p>
            </div>
            <button
              type="button"
              className="mt-3 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              onClick={() => setShowDeleteConfirmation(true)}
            >
              <FiTrash2 className="-ml-1 mr-2 h-5 w-5" />
              Delete Account
            </button>
          </div>
          
          {/* Delete Confirmation Dialog */}
          {showDeleteConfirmation && (
            <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <FiAlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    <strong>Warning:</strong> You are about to delete {user.full_name}'s account ({user.email}).
                    This action cannot be undone and will permanently delete all associated data.
                  </p>
                  <div className="mt-4">
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        onClick={handleDeleteUser}
                        disabled={updating}
                      >
                        {updating ? 'Deleting...' : 'Delete User'}
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        onClick={() => setShowDeleteConfirmation(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetails;